import React from 'react';
import {
  Button,
  Chip,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReturnIcon } from '@marageti/z4-lib';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { CountryVisited } from '@marageti/z4-sdk/lib/people';
import { YEAR_UNKNOWN_SENTINEL } from '../traveler-details/traveler-personal-details.utils';

export type TravelerProfileCountriesViewProps = {
  countriesVisited: CountryVisited[];
  countryCodes: CountryCodeMap | undefined;
  onEdit?: () => void;
};

const TravelerProfileCountriesView = ({
  countriesVisited,
  countryCodes,
  onEdit,
}: TravelerProfileCountriesViewProps) => {
  const hasCountries = countriesVisited.length > 0;
  const actionLabel = hasCountries ? 'Edit Countries' : '+Add Countries';

  // Collect all non-sentinel years per (country, zicasso) pair
  const yearsByKey = countriesVisited.reduce<Record<string, number[]>>((acc, cv) => {
    const key = `${cv.country}-${cv.zicasso}`;
    const year = cv.date ? new Date(cv.date).getUTCFullYear() : null;
    if (!acc[key]) acc[key] = [];
    if (year && year !== YEAR_UNKNOWN_SENTINEL) acc[key].push(year);
    return acc;
  }, {});

  // Deduplicate by (country, zicasso) pair, then sort by most recent year descending
  const seenKeys = new Set<string>();
  const countryEntries = [...countriesVisited]
    .reverse()
    .filter((cv) => {
      const key = `${cv.country}-${cv.zicasso}`;
      if (seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    })
    .sort((a, b) => {
      const maxYear = (cv: CountryVisited) => {
        const years = yearsByKey[`${cv.country}-${cv.zicasso}`] ?? [];
        return years.length ? Math.max(...years) : 0;
      };
      return maxYear(b) - maxYear(a);
    });

  return (
    <Stack spacing={2} width="100%">
      <Stack direction="row" alignItems="center" spacing={2}>
        <SvgIcon component={ReturnIcon} color="primary" fontSize="small" />
        <Typography variant="body">
          Countries Visited
        </Typography>
      </Stack>

      {hasCountries ? (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {countryEntries.map((cv) => {
            const key = `${cv.country}-${cv.zicasso}`;
            const allYears = yearsByKey[key] ?? [];
            const tooltipTitle = allYears.length > 0
              ? [...allYears].sort((a, b) => a - b).join(', ')
              : 'Unknown year';
            return (
              <Tooltip key={key} title={cv.zicasso ? `Zicasso - ${tooltipTitle}` : tooltipTitle} placement="top">
                <Chip
                  label={countryCodes?.[cv.country] ?? cv.country}
                  sx={{ backgroundColor: (theme) => (cv.zicasso ? theme.palette.common.info50 : theme.palette.common.beige50) }}
                />
              </Tooltip>
            );
          })}
        </Stack>
      ) : (
        <Typography variant="body" color="common.black50">
          Not recorded
        </Typography>
      )}

      {onEdit && (
        <Button
          variant="text"
          onClick={onEdit}
          sx={{
            typography: 'captionBold',
            color: 'primary.main',
            py: 0.5,
            px: 1,
            alignSelf: 'flex-start',
          }}
        >
          {actionLabel}
        </Button>
      )}
    </Stack>
  );
};

export default TravelerProfileCountriesView;
